import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useFormContext } from "react-hook-form";
import { getFromStatisticsAdsCampaign } from "../../../../../services/adsCampaigns";
import { TIKTOK } from "../../../../../utils/constants";
import {
  VALUE_NUMBER,
  VALUE_WITH_PERCENTAGE,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD_GROUP,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD,
  IMPRESSIONS,
  RESULTS,
  CTR,
  CLICKS,
  AGE_RANGE_PLACEHOLDER,
  GENDERS_PLACEHOLDER,
  MALES,
  FEMALES,
  LOCATIONS_PLACEHOLDER,
  INTERESTS_PLACEHOLDER,
  BEHAVIORS_PLACEHOLDER,
  CREATIVE,
} from "../../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../../lib/i18next";
import Grid from "@mui/material/Grid";
import PerformingAssetsCard from "../components/PerformingAssetsCard";
import { openLink } from "../../../../../utils/string";
import CardMedia from "../../../../../components/Media/CardMedia";
import _ from "lodash";
import { INPUT_COLOR_BORDER } from "../../../../../utils/colors";
import ModalMedia from "../../../../../components/Modal/ModalMedia";
import { useState } from "react";

const TiktokPerformingAssets = () => {
  const { t } = useTranslationApp();
  const { campaign, campaignID } = useFormContext();
  const { statistics } = getFromStatisticsAdsCampaign(campaign);

  // Returns the best ad group, prioritizing mainAction, otherwise by click
  const getBestAdGroup = (adGroupsStatistics) => {
    if (!adGroupsStatistics) return null;

    let bestByMainAction = null;
    let bestByClick = null;
    let hasMainAction = false;

    Object.entries(adGroupsStatistics).forEach(([adGroupId, stats]) => {
      if (stats.mainAction && stats.mainAction > 0) {
        hasMainAction = true;
        if (
          !bestByMainAction ||
          stats.mainAction > bestByMainAction.statistics.mainAction
        ) {
          bestByMainAction = {
            adGroupId,
            statistics: stats,
          };
        }
      }
      if (
        !bestByClick ||
        (stats.click || 0) > (bestByClick.statistics.click || 0)
      ) {
        bestByClick = {
          adGroupId,
          statistics: stats,
        };
      }
    });

    return hasMainAction ? bestByMainAction : bestByClick;
  };

  // Returns the best ad, prioritizing mainAction, otherwise by click
  const getBestAd = (adsGroups, statistics) => {
    if (!adsGroups || !statistics) return null;

    let bestAdByMainAction = null;
    let bestAdByClicks = null;
    let hasMainAction = false;

    adsGroups.forEach((adGroup) => {
      if (adGroup.platform !== TIKTOK) return;

      adGroup.ads.forEach((ad) => {
        const adStats = statistics[ad.id];
        if (!adStats) return;

        if (adStats.mainAction && adStats.mainAction > 0) {
          hasMainAction = true;
          if (
            !bestAdByMainAction ||
            adStats.mainAction > bestAdByMainAction.statistics.mainAction
          ) {
            bestAdByMainAction = {
              adGroupId: adGroup.id,
              adId: ad.id,
              statistics: adStats,
            };
          }
        }

        if (
          !bestAdByClicks ||
          (adStats.click || 0) > (bestAdByClicks.statistics.click || 0)
        ) {
          bestAdByClicks = {
            adGroupId: adGroup.id,
            adId: ad.id,
            statistics: adStats,
          };
        }
      });
    });

    return hasMainAction ? bestAdByMainAction : bestAdByClicks;
  };

  const bestAdGroup = getBestAdGroup(statistics[TIKTOK].adsGroups);
  const bestAd = getBestAd(campaign.adsGroups, statistics[TIKTOK].ads);

  const getAdGroupByID = (id) => {
    if (!id) {
      return {};
    }
    const adGroupTikTok = campaign.adsGroups.filter(
      (adGroup) => adGroup.platform === TIKTOK
    );
    const adGroup = adGroupTikTok.find((adGroup) => adGroup.id === id);

    return { adGroup, adGroupID: adGroup.id };
  };

  const getAdByID = (adId) => {
    if (!adId) {
      return {};
    }
    const adGroupsTikTok = campaign.adsGroups.filter(
      (adGroup) => adGroup.platform === TIKTOK
    );

    const foundAdGroup = adGroupsTikTok.find((adGroup) =>
      adGroup.ads.some((ad) => ad.id === adId)
    );

    if (!foundAdGroup) {
      return {};
    }

    const ad = foundAdGroup.ads.find((ad) => ad.id === adId);
    const adGroupIndex = adGroupsTikTok.findIndex(
      (group) => group.id === foundAdGroup.id
    );
    const adGroupID = foundAdGroup.id;

    return {
      ad,
      adID: ad.id,
      adGroupIndex,
      adGroupID,
    };
  };

  const { adGroup = {}, adGroupID } = getAdGroupByID(bestAdGroup?.adGroupId);
  const { ad = {}, adID, adGroupID: adGroupIDAd } = getAdByID(bestAd?.adId);

  const bestAdGroupStats = !_.isEmpty(bestAdGroup)
    ? [
        {
          label: t(IMPRESSIONS),
          value: t(VALUE_NUMBER, { value: bestAdGroup.statistics.impressions }),
        },
        {
          label: t(CLICKS),
          value: t(VALUE_NUMBER, { value: bestAdGroup.statistics.click }),
        },
        {
          label: t(CTR),
          value: t(VALUE_WITH_PERCENTAGE, {
            value: bestAdGroup.statistics.ctr,
          }),
        },
        {
          label: t(RESULTS),
          value: t(VALUE_NUMBER, { value: bestAdGroup.statistics.mainAction }),
        },
      ]
    : [];

  const bestAdStats = !_.isEmpty(bestAd)
    ? [
        {
          label: t(IMPRESSIONS),
          value: t(VALUE_NUMBER, { value: bestAd.statistics.impressions }),
        },
        {
          label: t(CLICKS),
          value: t(VALUE_NUMBER, { value: bestAd.statistics.click }),
        },
        {
          label: t(CTR),
          value: t(VALUE_WITH_PERCENTAGE, { value: bestAd.statistics.ctr }),
        },
        {
          label: t(RESULTS),
          value: t(VALUE_NUMBER, { value: bestAd.statistics.mainAction }),
        },
      ]
    : [];

  return (
    <Box sx={{ display: "flex", gap: 2, flexWrap: "nowrap", width: "100%" }}>
      {!_.isEmpty(bestAdGroup) && (
        <PerformingAssetsCard
          platform={TIKTOK}
          title={t(ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD_GROUP)}
          subtitle={adGroup.name}
          onSubtitleClick={() =>
            openLink(
              `ads-campaign?campaignID=${campaignID}&platform=${TIKTOK}&adGroupID=${adGroupID}`
            )
          }
          statistics={bestAdGroupStats}
          previousContent={<BestAdGroupSegmentation adGroup={adGroup} />}
          sx={{
            backgroundColor: "#1E1E1E",
            border: `1px solid ${INPUT_COLOR_BORDER}`,
            flex: 1,
            minWidth: 0,
          }}
        />
      )}

      {!_.isEmpty(bestAd) && (
        <PerformingAssetsCard
          platform={TIKTOK}
          title={t(ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD)}
          subtitle={ad.name}
          onSubtitleClick={() =>
            openLink(
              `ads-campaign?campaignID=${campaignID}&platform=${TIKTOK}&adGroupID=${adGroupIDAd}&adID=${adID}`
            )
          }
          statistics={bestAdStats}
          nextContent={<BestAdCreative ad={ad} />}
          sx={{
            backgroundColor: "#1E1E1E",
            border: `1px solid ${INPUT_COLOR_BORDER}`,
            flex: 1,
            minWidth: 0,
          }}
        />
      )}
    </Box>
  );
};

const SEGMENTATION_FIELDS = [
  "ageRange",
  "genders",
  "locations",
  "interests",
  "behaviors",
];
const BestAdGroupSegmentation = ({ adGroup = {} }) => {
  const { t } = useTranslationApp();

  const getSegmentationFields = () => {
    const segmentationFieldsData = [];

    SEGMENTATION_FIELDS.forEach((field) => {
      const fieldData = adGroup[field];
      if (!fieldData || fieldData?.length === 0) return;
      if (field === "ageRange") {
        const [minAge, maxAge] = fieldData || [];
        segmentationFieldsData.push({
          label: t(AGE_RANGE_PLACEHOLDER),
          value: `${minAge}-${maxAge}`,
        });
      } else if (field === "genders") {
        const genders = fieldData || {};
        segmentationFieldsData.push({
          label: t(GENDERS_PLACEHOLDER),
          value: Object.keys(genders)
            .filter((gender) => genders[gender] === true)
            .map((gender) => t(gender === "male" ? MALES : FEMALES))
            .join(", "),
        });
      } else {
        const value = fieldData.map((item) => item.name).join(", ");
        let label = "";
        switch (field) {
          case "locations":
            label = t(LOCATIONS_PLACEHOLDER);
            break;
          case "interests":
            label = t(INTERESTS_PLACEHOLDER);
            break;
          case "behaviors":
            label = t(BEHAVIORS_PLACEHOLDER);
            break;
        }

        segmentationFieldsData.push({
          label,
          value,
        });
      }
    });

    return segmentationFieldsData;
  };

  const segmentationFields = getSegmentationFields();

  return (
    <Grid container spacing={1} sx={{ mb: 2 }}>
      {segmentationFields.map((field) => (
        <Grid item xs={6} key={field.label}>
          <Box>
            <Typography variant="caption" color="fields.placeholder">
              {field.label}
            </Typography>
            <Typography variant="body2">{field.value}</Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
};

const BestAdCreative = ({ ad = {} }) => {
  const { t } = useTranslationApp();
  const [previewMediaUrl, setPreviewMediaUrl] = useState(null);

  const firstMediaUrl = ad?.mediaUrls?.[0];

  if (!firstMediaUrl) return null;

  const onMediaClick = () => {
    setPreviewMediaUrl(firstMediaUrl);
  };

  const onClosePreview = () => {
    setPreviewMediaUrl(null);
  };

  return (
    <>
      <Box>
        <Typography variant="subtitle1" color="fields.placeholder">
          {t(CREATIVE)}
        </Typography>
        <Box
          onClick={onMediaClick}
          sx={{
            cursor: "pointer",
            transition: "transform 0.2s ease",
            "&:hover": {
              transform: "scale(1.02)",
            },
          }}
        >
          <CardMedia
            url={firstMediaUrl}
            sx={{
              width: "auto",
              height: 200,
              objectFit: "contain",
            }}
          />
        </Box>
      </Box>

      {/* Modal de preview de media */}
      {previewMediaUrl && (
        <ModalMedia
          modalOpen={!!previewMediaUrl}
          onCloseModal={onClosePreview}
          url={previewMediaUrl}
          showEditButton={false}
          showShareButton={false}
          showDownloadButton={false}
        />
      )}
    </>
  );
};

export default TiktokPerformingAssets;
